// Минималистичный SVG donut chart. Без зависимостей.
// Цвет сегмента — токен --cat-N по стабильному хешу slug: тот же индекс,
// что и у монограммы категории, и он же корректен в обеих темах.

import { useState } from 'react';
import { categoryTintIndex } from '../../lib/colors';
import { formatWithCurrency } from '../../lib/money';

export type DonutSegment = {
  key: string;
  label: string;
  amountMinor: string;
  pct: number;
  slug: string | null;
};

type Props = {
  segments: DonutSegment[];
  totalMinor: string;
  currency: string;
  kind: 'expense' | 'income';
  onSegmentClick?: (seg: DonutSegment) => void;
};

const SIZE = 220;
const STROKE = 26;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const GAP_DEG = 1.5; // градусы между сегментами

export function DonutChart({
  segments,
  totalMinor,
  currency,
  kind,
  onSegmentClick,
}: Props): JSX.Element {
  const [hovered, setHovered] = useState<string | null>(null);

  // Если только один сегмент или все нулевые — показываем кольцо одного цвета.
  const visible = segments.filter((s) => Number(s.pct) > 0);
  const totalPct = visible.reduce((acc, s) => acc + s.pct, 0);
  const safe = totalPct > 0 ? visible : [];

  // Раскладываем в дуги. SVG strokeDasharray pattern: [сегмент, gap].
  // Используем единое кольцо с rotate — каждый сегмент отдельным <circle>.
  let cumulativeDeg = -90; // старт сверху

  const focused = hovered
    ? visible.find((s) => s.key === hovered)
    : null;
  const centerAmount = focused ? focused.amountMinor : totalMinor;
  const centerLabel = focused ? focused.label : kind === 'expense' ? 'Всего трат' : 'Всего доходов';

  return (
    <div className="donut-wrap">
      <svg
        width={SIZE}
        height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        className="donut-svg"
        role="img"
        aria-label={`Кольцевая диаграмма: ${centerLabel}`}
      >
        {/* Базовое серое кольцо как fallback при отсутствии данных */}
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke="var(--rule)"
          strokeWidth={STROKE}
        />
        {safe.map((s) => {
          const segDeg = (s.pct / 100) * 360 - GAP_DEG;
          const segLen = (Math.max(segDeg, 0) / 360) * CIRCUMFERENCE;
          const dashArray = `${segLen} ${CIRCUMFERENCE}`;
          // strokeDashoffset двигает стартовую точку.
          const offset = (-cumulativeDeg / 360) * CIRCUMFERENCE + 90 / 360 * CIRCUMFERENCE;
          const color = `var(--cat-${categoryTintIndex(s.slug ?? s.key)})`;
          const startDeg = cumulativeDeg;
          cumulativeDeg += (s.pct / 100) * 360;
          const isHovered = hovered === s.key;
          // Замечание: используем transform-rotate, потому что dashoffset с разными размерами
          // даёт неточную позицию из-за gap. Rotate проще и точнее.
          void offset;
          void startDeg;
          return (
            <circle
              key={s.key}
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={RADIUS}
              fill="none"
              strokeWidth={isHovered ? STROKE + 4 : STROKE}
              strokeDasharray={dashArray}
              strokeLinecap="butt"
              transform={`rotate(${startDeg} ${SIZE / 2} ${SIZE / 2})`}
              style={{
                stroke: color,
                transition: 'stroke-width 0.15s ease, opacity 0.15s ease',
                opacity: hovered && !isHovered ? 0.5 : 1,
                cursor: onSegmentClick ? 'pointer' : 'default',
              }}
              onMouseEnter={() => setHovered(s.key)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => onSegmentClick?.(s)}
            />
          );
        })}
      </svg>
      <div className="donut-center">
        <div className="donut-center-label">{centerLabel}</div>
        <div className="donut-center-value">
          {formatWithCurrency(centerAmount, currency)}
        </div>
        {focused ? (
          <div className="donut-center-pct">{focused.pct.toFixed(1)}%</div>
        ) : null}
      </div>
    </div>
  );
}
